import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout } from '@Styled/utilities';
import SearchIcon from '@mui/icons-material/Search';
import {
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import SubmissionsPager from '../../Submissions/Components/SubmissionsPager';
import useStandalonePageLoader from '../useStandalonePageLoader';
import usePagedSubmissions, { PagedResponse } from '../../Submissions/Hooks/usePagedSubmissions';
import AssignmentSubmissionCard from './AssignmentSubmissionCard';
import SubmissionModal from './SubmissionModal';
import { AssignmentSubmission } from './Submissions.types';

type LectureOption = { id: number; name: string };
type CourseLike = {
	id: number;
	tabs?: Array<{ sections?: Array<{ courses?: LectureOption[] }> }>;
	unsectioned_lectures?: LectureOption[];
};

type Props = { course: CourseLike };

const AssignmentSubmissionsTable: React.FC<Props> = ({ course }) => {
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const [searchInput, setSearchInput] = useState('');
	const [query, setQuery] = useState('');
	const [lectureId, setLectureId] = useState<number | ''>('');
	const [reviewed, setReviewed] = useState(false);
	const [currentSubmission, setCurrentSubmission] = useState<AssignmentSubmission>();

	const lectures = useMemo<LectureOption[]>(
		() =>
			(course.tabs || [])
				.flatMap((tab) => tab.sections || [])
				.flatMap((section) => section.courses || [])
				.concat(course.unsectioned_lectures || []),
		[course.tabs, course.unsectioned_lectures]
	);

	const { page, setPage, rows: submissions, meta, reload } = usePagedSubmissions<AssignmentSubmission>(
		(activePage) =>
			withLoading(async () => {
				const response = await api.post('standalone-courses/submissions', {
					standalone_course_id: course.id,
					page: activePage,
					perPage: 10,
					submitted: reviewed,
					query: query || undefined,
					...(lectureId ? { course_id: lectureId } : {}),
				});
				return response.data as PagedResponse<AssignmentSubmission>;
			}),
		[course.id, query, lectureId, reviewed],
		(error: any) => displaySnackbar('error', error?.response?.data?.message || 'Unable to load assignment submissions')
	);

	const applySearch = () => setQuery(searchInput.trim());

	const closeModal = () => {
		setCurrentSubmission(undefined);
		reload();
	};

	return (
		<div>
			{currentSubmission && (
				<SubmissionModal
					open
					submission={currentSubmission}
					onClose={closeModal}
				/>
			)}
			<Controls>
				<FlexLayout alignItems="center" gridGap="1rem">
					<TextField
						select
						size="small"
						label="Lecture"
						value={lectureId}
						sx={{ minWidth: '14rem' }}
						onChange={(e) => setLectureId(e.target.value === '' ? '' : Number(e.target.value))}
					>
						<MenuItem value="">All Lectures</MenuItem>
						{lectures.map((lecture) => (
							<MenuItem key={lecture.id} value={lecture.id}>
								{lecture.name}
							</MenuItem>
						))}
					</TextField>
					<ToggleButtonGroup
						exclusive
						size="small"
						value={reviewed}
						onChange={(_, value) => {
							if (value === null) return;
							setReviewed(value);
						}}
					>
						<ToggleButton value={false}>Unreviewed</ToggleButton>
						<ToggleButton value={true}>Reviewed</ToggleButton>
					</ToggleButtonGroup>
				</FlexLayout>
				<FlexLayout alignItems="center">
					<TextField
						size="small"
						placeholder="Search students"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') applySearch();
						}}
					/>
					<IconButton onClick={applySearch} aria-label="search">
						<SearchIcon />
					</IconButton>
				</FlexLayout>
			</Controls>
			{submissions.length === 0 ? (
				<Empty>
					{reviewed
						? 'No reviewed assignment submissions'
						: 'No assignment submissions awaiting review'}
				</Empty>
			) : (
				submissions.map((submission) => (
					<AssignmentSubmissionCard
						key={submission.id}
						submission={submission}
						actionLabel={submission.score == null ? 'Score' : 'Regrade'}
						onClick={(row) => setCurrentSubmission(row)}
					/>
				))
			)}
			<SubmissionsPager meta={meta} page={page} shown={submissions.length} onChange={setPage} />
		</div>
	);
};

const Controls = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;
	margin-bottom: 1.5rem;
`;

const Empty = styled(Typography)`
	color: #a4a4a4;
	padding: 2rem 0;
	text-align: center;
`;

export default AssignmentSubmissionsTable;
